'use client'

import { getUserFromLocalStorage } from '@/utils/storage'
import { Agency, AgencyUser, CreditWallet, User, RoleName } from '@prisma/client'
import axios, { AxiosInstance } from 'axios'
import { createClient } from '@/utils/supabase/client'

// Re-export RoleName type from Prisma for backward compatibility
export type { RoleName }

interface IgetAgencyUserResponse {
  success: boolean
  agencyUserId: {
    id: string
    userId: string
    agencyId: string
  }
  error: string
}

export interface InviteUserData {
  subaccountId: string
  landingId: string
}

export interface AgencyUserType extends AgencyUser {
  agency?: Agency & {
    creditWallet: CreditWallet
  }
}

export const getUserAccessToken = () => {
  const userObject = getUserFromLocalStorage()
  if (!userObject || !userObject.userInfo || !userObject.userInfo.session) {
    return null
  }

  return userObject.userInfo.session.access_token
}

// New function to get the real Supabase access token
export const getSupabaseAccessToken = async () => {
  try {
    const supabase = createClient()
    const { data: { session }, error } = await supabase.auth.getSession()
    
    if (error) {
      console.error('Error getting Supabase session:', error)
      return null
    }
    
    return session?.access_token || null
  } catch (error) {
    console.error('Error accessing Supabase session:', error)
    return null
  }
}

// Updated function that tries Supabase first, then falls back to localStorage
export const getCurrentAccessToken = async () => {
  // First try to get the real Supabase token
  const supabaseToken = await getSupabaseAccessToken()
  if (supabaseToken) {
    return supabaseToken
  }
  
  // Fallback to localStorage token
  return getUserAccessToken()
}

export const setUserAccessToken = (
  newAccessToken: string,
  refreshToken: string
) => {
  const userObject = getUserFromLocalStorage()
  if (!userObject) return

  userObject.userInfo.session.access_token = newAccessToken
  userObject.userInfo.session.refresh_token = refreshToken

  localStorage.setItem('user', JSON.stringify(userObject))
}

export const getUserSession = () => {
  const userObject = getUserFromLocalStorage()
  if (!userObject) return null

  return userObject.userInfo.session
}

export const getUserData = (): User | null => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null // localStorage not available (e.g., SSR)
  }
  const user = localStorage.getItem('user')
  if (!user) return null

  try {
    const parsedUser = JSON.parse(user);
    return parsedUser?.userInfo?.user ?? null;
  } catch (err) {
    console.error('Error getting UserData:', err)
    return null;
  }
}

export const getUserProviderIdentities = () => {
  const userObj = getUserFromLocalStorage()
  return userObj?.userInfo.session.user.identities
}

export const getAgencyUserData = (): AgencyUserType | null => {
  const userObj = getUserFromLocalStorage()
  if (!userObj) return null

  return userObj.agencyUser
}

export const removeUser = () => {
  localStorage.removeItem('user')
}

export const getAgencyUser = (): IgetAgencyUserResponse | null => {
  const user = getUserData()
  const agencyUser = getAgencyUserData()
  // returning this data to maintain previous logic
  return {
    success: true,
    agencyUserId: {
      id: agencyUser?.id || '',
      userId: user?.userId || '',
      agencyId: agencyUser?.agencyId || ''
    },
    error: ''
  }
}

export const updateAvatar = (url: string) => {
  const storedData = localStorage.getItem('user')
  if (!storedData) return

  try {
    const parsedData = JSON.parse(storedData)
    if (parsedData?.userInfo?.user) {
      parsedData.userInfo.user.avatarUrl = url
      localStorage.setItem('user', JSON.stringify(parsedData))
    }
  } catch (error) {
    console.error('Failed to update avatar URL:', error)
  }
}

export const updateSubscriptionStatus = (subscriptionStatus: string) => {
  const storedData = localStorage.getItem('user')
  if (!storedData) return

  try {
    const parsedData = JSON.parse(storedData)

    if (parsedData?.userInfo?.user) {
      parsedData.userInfo.user.subscriptionStatus = subscriptionStatus

      localStorage.setItem('user', JSON.stringify(parsedData))
    }
  } catch (error) {
    console.error('Failed to update user data:', error)
  }
}

export const attachTokenRefreshInterceptor = (client: AxiosInstance) => {
  let isRefreshing = false
  let failedQueue: {
    resolve: (value?: unknown) => void
    reject: (error: unknown) => void
  }[] = []

  const processQueue = (error: unknown, token: string | null = null) => {
    failedQueue.forEach(prom => {
      if (token) {
        prom.resolve(token)
      } else {
        prom.reject(error)
      }
    })
    failedQueue = []
  }

  client.interceptors.response.use(
    res => res,
    async error => {
      const originalRequest = error.config

      if (error.response?.status === 403 && !originalRequest._retry) {
        originalRequest._retry = true

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject })
          })
            .then(token => {
              originalRequest.headers.Authorization = `Bearer ${token}`
              return client(originalRequest)
            })
            .catch(err => Promise.reject(err))
        }

        isRefreshing = true
        try {
          const response = await axios.post(
            `${process.env.NEXT_PUBLIC_MATOMO_API_URL}/auth/refresh`,
            null,
            { withCredentials: true }
          )

          if (response?.data?.success) {
            const { access_token, refresh_token } = response.data.data.session
            setUserAccessToken(access_token, refresh_token)
            processQueue(null, access_token)

            originalRequest.headers.Authorization = `Bearer ${access_token}`
            return client(originalRequest)
          }
        } catch (refreshError) {
          processQueue(refreshError, null)
          console.error("User Sign-out: Error while refreshing token", refreshError)
          window.location.href = '/agency/signin'
          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false
        }
      }
      return Promise.reject(error)
    }
  )
}
